// Business detail sheet (full info, promo, navigate button)
import React, { useState, useEffect } from "react";
import { useI18n } from "@/lib/i18n";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, Globe, MapPin, Navigation2, Clock, Heart, Sparkles, X, Mountain, Route, Timer, TrendingUp, RotateCcw, ChevronRight, List, Download, Compass, ExternalLink } from "lucide-react";
import { downloadGpx } from "@/lib/gpxExport";
import TrailFollower from "@/components/TrailFollower";
import { trackBusinessView, trackNavigateClick, trackGpxDownload, trackTrailFollowStart } from "@/lib/analytics";

function formatDistance(km) {
  if (km == null) return "—";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(2)} km`;
}

// Open route in Komoot for turn-by-turn navigation
function openInKomoot(geojsonData, isCycling = false) {
  if (!geojsonData?.geometry?.coordinates?.length) return;

  const coords = geojsonData.geometry.coordinates;
  const startCoord = coords[0];
  const endCoord = coords[coords.length - 1];

  // Komoot uses [lng, lat] in GeoJSON but needs lat,lng in URL
  const startLat = startCoord[1];
  const startLng = startCoord[0];
  const endLat = endCoord[1];
  const endLng = endCoord[0];

  // Sport type: racebike, mtb, touringbicycle, or hike
  const sport = isCycling ? "touringbicycle" : "hike";

  // Komoot deep link URL
  const komootUrl = `https://www.komoot.com/plan/@${startLat},${startLng},12z?sport=${sport}`;

  // Try to open in app first (mobile), fallback to web
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    // Try komoot:// deep link for mobile app
    const appUrl = `komoot://www.komoot.com/plan/@${startLat},${startLng},12z?sport=${sport}`;

    // Create a hidden iframe to try the app URL
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = appUrl;
    document.body.appendChild(iframe);

    // Fallback to web after short delay
    setTimeout(() => {
      document.body.removeChild(iframe);
      window.open(komootUrl, '_blank');
    }, 1500);
  } else {
    window.open(komootUrl, '_blank');
  }
}

export default function BusinessDetail({ open, onClose, business, onNavigate, onToggleFavorite, isFavorite, allBusinesses = [], onSelectBusiness }) {
  const { t, lang } = useI18n();
  const [showTappeList, setShowTappeList] = useState(false);
  const [showTrailFollower, setShowTrailFollower] = useState(false);

  // Track business view when detail opens
  useEffect(() => {
    if (open && business) {
      trackBusinessView(business);
    }
  }, [open, business]);

  // Helper to get translated field based on current language
  const getTranslatedField = (fieldName) => {
    if (!business) return "";
    if (lang === "it") return business[fieldName] || "";
    const translatedField = `${fieldName}_${lang}`;
    return business[translatedField] || business[fieldName] || "";
  };

  // Check if business has GPS track (for individual sentieri)
  const hasGpsTrack = business?.geojson_data?.geometry?.coordinates?.length > 0;

  // Get other tappe of the same cammino
  const camminoTappe = React.useMemo(() => {
    if (!business?.cammino_name || !allBusinesses.length) return [];
    return allBusinesses
      .filter((b) => b.cammino_name === business.cammino_name)
      .sort((a, b) => {
        const numA = parseInt(a.tappa_number) || 999;
        const numB = parseInt(b.tappa_number) || 999;
        return numA - numB;
      });
  }, [business, allBusinesses]);

  // Find cammino start tappa with GPS track (for cammini)
  const camminoStart = React.useMemo(() => {
    if (!business?.cammino_name || !camminoTappe.length) return null;
    return camminoTappe.find(t => t.is_cammino_start && t.geojson_data?.geometry?.coordinates?.length > 0);
  }, [business, camminoTappe]);

  // Has cammino GPS track (from start tappa)
  const hasCamminoTrack = !!camminoStart;

  // Find Ciclo Turismo main route with GPS track (for tappe)
  const cicloturismoMainRoute = React.useMemo(() => {
    if (business?.category !== "Ciclo Turismo" || !allBusinesses.length) return null;
    // Find the main route (Ciclovia Parchi Calabria) that has geojson_data
    return allBusinesses.find(b =>
      b.category === "Ciclo Turismo" &&
      b.name?.includes("Ciclovia") &&
      b.geojson_data?.geometry?.coordinates?.length > 0
    );
  }, [business, allBusinesses]);

  // Has Ciclo Turismo GPS track (from main route)
  const hasCicloturismoTrack = !!cicloturismoMainRoute;

  if (!business) return null;

  // Use business directly (no external translation API)
  const biz = business;

  const inProx = !!business.in_proximity;
  const base = Number(business.base_discount || 0);
  const bonus = Number(business.proximity_discount || 0);
  const eff = business.effective_discount != null ? Number(business.effective_discount) : base;

  return (
    <>
    <Sheet open={open}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 overflow-y-auto rounded-t-3xl sm:rounded-l-3xl sm:rounded-t-none"
        data-testid="business-detail-sheet"
        hideOverlay={true}
        hideCloseButton={true}
      >
        {/* Close button - prominent at top */}
        <div className="p-4 pb-2 flex items-center justify-between border-b border-[var(--border)]">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-[var(--primary)] font-medium"
            data-testid="business-detail-close"
          >
            <X className="w-5 h-5" />
            <span>{t("back")}</span>
          </button>
          <div className="flex items-center gap-2">
            {eff > 0 && business.category !== "Sentieri e Cammini" && (
              <div className={`px-3 py-1.5 rounded-full text-white font-bold text-sm ${inProx ? "bg-[var(--primary)]" : "bg-[var(--secondary)]"}`}
                data-testid="proximity-discount-badge"
              >
                -{Math.round(eff)}%
              </div>
            )}
          </div>
        </div>

        <div className="p-5 pt-4 pb-0">
          <Badge className="bg-[var(--bg)] text-[var(--text-secondary)] rounded-full mb-2">
            {business.category === "Sentieri e Cammini" ? t("trails_and_paths") : business.category}
          </Badge>
          <h2 className="font-display text-xl font-semibold text-[var(--text-primary)]">{biz.name}</h2>
          {(getTranslatedField("description") || getTranslatedField("promotion_description")) && (
            <p className="text-sm text-[var(--text-secondary)] mt-1">{getTranslatedField("description") || getTranslatedField("promotion_description")}</p>
          )}
        </div>

        <SheetHeader className="sr-only">
          <SheetTitle>{biz.name}</SheetTitle>
          <SheetDescription>{business.category}</SheetDescription>
        </SheetHeader>

        <div className="p-5 space-y-4">
          {/* Ciclo Turismo info panel */}
          {business.category === "Ciclo Turismo" && (business.difficulty || business.distance || business.duration || business.cammino_name) && (
            <div className="rounded-2xl p-4 bg-gradient-to-r from-cyan-50 to-teal-50 border border-cyan-200">
              {business.cammino_name && (
                <div className="flex items-center gap-2 mb-3">
                  <Route className="w-4 h-4 text-cyan-600" />
                  <span className="text-sm font-semibold text-cyan-700">{business.cammino_name}</span>
                  {business.tappa_number && (
                    <Badge className="bg-cyan-100 text-cyan-700 text-xs">{t("stage")} {business.tappa_number}</Badge>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {business.difficulty && (
                  <div className="flex items-center gap-2">
                    <Mountain className="w-4 h-4 text-teal-500" />
                    <div>
                      <div className="text-xs text-teal-600 uppercase tracking-wide">{t("difficulty")}</div>
                      <div className="text-sm font-medium text-teal-800 capitalize">{business.difficulty}</div>
                    </div>
                  </div>
                )}
                {business.distance && (
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-cyan-500" />
                    <div>
                      <div className="text-xs text-cyan-600 uppercase tracking-wide">{t("trail_distance")}</div>
                      <div className="text-sm font-medium text-cyan-800">{business.distance}</div>
                    </div>
                  </div>
                )}
                {business.duration && (
                  <div className="flex items-center gap-2">
                    <Timer className="w-4 h-4 text-teal-500" />
                    <div>
                      <div className="text-xs text-teal-600 uppercase tracking-wide">{t("duration")}</div>
                      <div className="text-sm font-medium text-teal-800">{business.duration}</div>
                    </div>
                  </div>
                )}
                {business.elevation_gain && (
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-cyan-500" />
                    <div>
                      <div className="text-xs text-cyan-600 uppercase tracking-wide">{t("elevation_gain")}</div>
                      <div className="text-sm font-medium text-cyan-800">{business.elevation_gain} m</div>
                    </div>
                  </div>
                )}
              </div>
              {business.geojson_data && (
                <div className="mt-3 text-xs text-cyan-600 flex items-center gap-1">
                  <span className="w-3 h-1 bg-cyan-500 rounded-full"></span>
                  {t("gps_track_available")}
                </div>
              )}
              {!business.geojson_data && hasCicloturismoTrack && (
                <div className="mt-3 text-xs text-cyan-600 flex items-center gap-1">
                  <span className="w-3 h-1 bg-cyan-500 rounded-full"></span>
                  {t("gps_track_full_available")}
                </div>
              )}
            </div>
          )}

          {/* Trail info panel - only for Sentieri e Cammini */}
          {business.category === "Sentieri e Cammini" && (business.difficulty || business.distance || business.duration || business.cammino_name) && (
            <div className="rounded-2xl p-4 bg-gradient-to-r from-orange-50 to-sky-50 border border-orange-200">
              {business.cammino_name && (
                <div className="flex items-center gap-2 mb-3">
                  <Route className="w-4 h-4 text-sky-600" />
                  <span className="text-sm font-semibold text-sky-700">{business.cammino_name}</span>
                  {business.tappa_number && (
                    <Badge className="bg-sky-100 text-sky-700 text-xs">
                      {business.tappa_number.includes(";")
                        ? `${t("stages")} ${business.tappa_number.replace(/;/g, ", ")}`
                        : `${t("stage")} ${business.tappa_number}`}
                    </Badge>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {business.difficulty && (
                  <div className="flex items-center gap-2">
                    <Mountain className="w-4 h-4 text-orange-500" />
                    <div>
                      <div className="text-xs text-orange-600 uppercase tracking-wide">{t("difficulty")}</div>
                      <div className="text-sm font-medium text-orange-800 capitalize">{business.difficulty}</div>
                    </div>
                  </div>
                )}
                {business.distance && (
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-sky-500" />
                    <div>
                      <div className="text-xs text-sky-600 uppercase tracking-wide">{t("trail_distance")}</div>
                      <div className="text-sm font-medium text-sky-800">{business.distance}</div>
                    </div>
                  </div>
                )}
                {business.duration && (
                  <div className="flex items-center gap-2">
                    <Timer className="w-4 h-4 text-orange-500" />
                    <div>
                      <div className="text-xs text-orange-600 uppercase tracking-wide">{t("duration")}</div>
                      <div className="text-sm font-medium text-orange-800">{business.duration}</div>
                    </div>
                  </div>
                )}
                {business.elevation_gain && (
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-sky-500" />
                    <div>
                      <div className="text-xs text-sky-600 uppercase tracking-wide">{t("elevation_gain")}</div>
                      <div className="text-sm font-medium text-sky-800">{business.elevation_gain} m</div>
                    </div>
                  </div>
                )}
              </div>
              {business.is_loop && (
                <div className="mt-3 flex items-center gap-2 text-orange-600">
                  <RotateCcw className="w-4 h-4" />
                  <span className="text-sm font-medium">{t("loop_trail")}</span>
                </div>
              )}
              {business.geojson_data && (
                <div className="mt-3 text-xs text-sky-600 flex items-center gap-1">
                  <span className="w-3 h-1 bg-orange-500 rounded-full"></span>
                  {t("gps_track_available")}
                </div>
              )}
              {!business.geojson_data && hasCamminoTrack && (
                <div className="mt-3 text-xs text-sky-600 flex items-center gap-1">
                  <span className="w-3 h-1 bg-orange-500 rounded-full"></span>
                  {t("gps_track_full_available")}
                </div>
              )}

              {/* Button to show all tappe of the cammino */}
              {camminoTappe.length > 1 && (
                <button
                  onClick={() => setShowTappeList(!showTappeList)}
                  className="mt-3 w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white/70 hover:bg-white text-sky-700 text-sm font-medium transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <List className="w-4 h-4" />
                    {t("view_all_stages").replace("{count}", camminoTappe.length)}
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showTappeList ? "rotate-90" : ""}`} />
                </button>
              )}

              {/* List of tappe */}
              {showTappeList && camminoTappe.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-sky-100">
                  {camminoTappe.map((tappa, index) => (
                    <button
                      key={tappa.business_id}
                      onClick={() => {
                        onSelectBusiness && onSelectBusiness(tappa.business_id);
                        setShowTappeList(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-sky-50 transition-colors ${
                        tappa.business_id === business.business_id ? "bg-sky-100 font-medium" : "bg-white"
                      } ${index > 0 ? "border-t border-sky-50" : ""}`}
                    >
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-orange-500 to-sky-500 text-white text-xs flex items-center justify-center font-bold">
                        {tappa.tappa_number || index + 1}
                      </span>
                      <span className="truncate">{tappa.name}</span>
                      {tappa.tappa_type && tappa.tappa_type !== "tappa" && tappa.tappa_type !== "borgo" && (
                        <Badge className="ml-auto bg-sky-100 text-sky-600 text-xs">{tappa.tappa_type}</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Proximity panel - hidden for trails and cycling routes */}
          {business.category !== "Sentieri e Cammini" && business.category !== "Ciclo Turismo" && (
            <div className={`rounded-2xl p-4 border ${inProx ? "border-[var(--primary)] bg-[var(--proximity)]" : "border-[var(--border)] bg-white"}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-[var(--text-secondary)] font-semibold">
                  {t("effective_discount")}
                </div>
                <div className="text-xs text-[var(--text-secondary)]">
                  {t("distance")}: <span className="font-semibold text-[var(--text-primary)]">{formatDistance(business.distance_km)}</span>
                </div>
              </div>
              <div className="mt-2 flex items-end gap-2">
                <div className="text-4xl font-display font-bold text-[var(--primary)]">{Math.round(eff)}%</div>
                <div className="text-sm text-[var(--text-secondary)] pb-1">
                  {t("base_discount")} {base}% {bonus > 0 && `+ ${t("proximity_discount")} ${bonus}%`}
                </div>
              </div>
              <div className={`mt-2 text-sm font-medium ${inProx ? "text-[var(--primary)]" : "text-[var(--text-secondary)]"}`}>
                {inProx ? `✓ ${t("within_radius")}` : t("outside_radius")}
              </div>
            </div>
          )}

          {getTranslatedField("promotion_title") && (
            <div className="rounded-2xl p-4 bg-[var(--warning)]/30 border border-[var(--warning)]">
              <div className="flex items-center gap-2 font-semibold text-[var(--text-primary)]">
                <Sparkles className="w-4 h-4 text-[var(--primary)]" />
                {getTranslatedField("promotion_title")}
              </div>
              {getTranslatedField("promotion_description") && (
                <div className="text-sm text-[var(--text-secondary)] mt-1">{getTranslatedField("promotion_description")}</div>
              )}
            </div>
          )}

          <div className="space-y-2 text-sm">
            {biz.address && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-[var(--text-secondary)]" />
                <span>{biz.address}{biz.city ? `, ${biz.city}` : ""}</span>
              </div>
            )}
            {biz.hours && (
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-[var(--text-secondary)]" />
                <span>{biz.hours}</span>
              </div>
            )}
            {business.phone && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-[var(--text-secondary)]" />
                <a href={`tel:${business.phone}`} className="text-[var(--primary)] hover:underline">{business.phone}</a>
              </div>
            )}
            {business.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 mt-0.5 text-[var(--text-secondary)]" />
                <a href={business.website} target="_blank" rel="noreferrer" className="text-[var(--primary)] hover:underline truncate">{business.website}</a>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button
              onClick={() => {
                trackNavigateClick(business);
                onNavigate(business);
              }}
              className="rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              data-testid="business-navigate-btn"
            >
              <Navigation2 className="w-4 h-4 mr-2" /> {t("navigate")}
            </Button>
            <Button
              variant="outline"
              onClick={onToggleFavorite}
              className="rounded-full border-[var(--border)]"
              data-testid="business-favorite-btn"
            >
              <Heart className={`w-4 h-4 mr-2 ${isFavorite ? "fill-[var(--primary)] text-[var(--primary)]" : ""}`} />
              {isFavorite ? t("remove_favorite") : t("add_favorite")}
            </Button>
          </div>

          {/* Trail-specific buttons for GPS tracks (individual sentieri or cicloturismo) */}
          {hasGpsTrack && (
            <>
              <div className="grid grid-cols-2 gap-2 pt-2" style={{ touchAction: "manipulation" }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    trackGpxDownload(business.name);
                    downloadGpx(business.geojson_data, business.name, business.description || "");
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    trackGpxDownload(business.name);
                    downloadGpx(business.geojson_data, business.name, business.description || "");
                  }}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-full border bg-white font-medium text-sm cursor-pointer select-none ${
                    business.category === "Ciclo Turismo"
                      ? "border-cyan-300 text-cyan-700 active:bg-cyan-100"
                      : "border-sky-300 text-sky-700 active:bg-sky-100"
                  }`}
                  style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                >
                  <Download className="w-4 h-4" /> {t("download_gpx")}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    trackTrailFollowStart(business.name);
                    setShowTrailFollower(true);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    trackTrailFollowStart(business.name);
                    setShowTrailFollower(true);
                  }}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-full text-white font-medium text-sm cursor-pointer select-none ${
                    business.category === "Ciclo Turismo"
                      ? "bg-gradient-to-r from-cyan-500 to-teal-500 active:from-cyan-700 active:to-teal-700"
                      : "bg-gradient-to-r from-orange-500 to-sky-500 active:from-orange-700 active:to-sky-700"
                  }`}
                  style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                >
                  <Compass className="w-4 h-4" /> {t("follow_trail")}
                </button>
              </div>
              {/* Komoot button for cycling routes */}
              {business.category === "Ciclo Turismo" && (
                <div className="pt-2" style={{ touchAction: "manipulation" }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openInKomoot(business.geojson_data, true);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      openInKomoot(business.geojson_data, true);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-green-400 text-green-700 bg-green-50 hover:bg-green-100 active:bg-green-200 font-medium text-sm cursor-pointer select-none"
                    style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                  >
                    <ExternalLink className="w-4 h-4" /> {t("open_in_komoot")}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Cammino-specific buttons for GPS tracks (when viewing any tappa) */}
          {!hasGpsTrack && hasCamminoTrack && (
            <div className="grid grid-cols-2 gap-2 pt-2" style={{ touchAction: "manipulation" }}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackGpxDownload(business.cammino_name);
                  downloadGpx(camminoStart.geojson_data, business.cammino_name, `Percorso completo - ${camminoTappe.length} tappe`);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  trackGpxDownload(business.cammino_name);
                  downloadGpx(camminoStart.geojson_data, business.cammino_name, `Percorso completo - ${camminoTappe.length} tappe`);
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-sky-300 text-sky-700 bg-white active:bg-sky-100 font-medium text-sm cursor-pointer select-none"
                style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              >
                <Download className="w-4 h-4" /> {t("download_gpx")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackTrailFollowStart(business.cammino_name);
                  setShowTrailFollower(true);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  trackTrailFollowStart(business.cammino_name);
                  setShowTrailFollower(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-orange-500 to-sky-500 active:from-orange-700 active:to-sky-700 text-white font-medium text-sm cursor-pointer select-none"
                style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              >
                <Compass className="w-4 h-4" /> {t("follow_cammino")}
              </button>
            </div>
          )}

          {/* Ciclo Turismo buttons for GPS tracks (main route or tappe) */}
          {business.category === "Ciclo Turismo" && !hasGpsTrack && hasCicloturismoTrack && (
            <div className="grid grid-cols-2 gap-2 pt-2" style={{ touchAction: "manipulation" }}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackGpxDownload(cicloturismoMainRoute.name);
                  downloadGpx(cicloturismoMainRoute.geojson_data, cicloturismoMainRoute.name, cicloturismoMainRoute.description || "Ciclovia Parchi Calabria - 545 km");
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  trackGpxDownload(cicloturismoMainRoute.name);
                  downloadGpx(cicloturismoMainRoute.geojson_data, cicloturismoMainRoute.name, cicloturismoMainRoute.description || "Ciclovia Parchi Calabria - 545 km");
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-cyan-300 text-cyan-700 bg-white active:bg-cyan-100 font-medium text-sm cursor-pointer select-none"
                style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              >
                <Download className="w-4 h-4" /> {t("download_gpx")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  trackTrailFollowStart(cicloturismoMainRoute.name);
                  setShowTrailFollower(true);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  trackTrailFollowStart(cicloturismoMainRoute.name);
                  setShowTrailFollower(true);
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 active:from-cyan-700 active:to-teal-700 text-white font-medium text-sm cursor-pointer select-none"
                style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              >
                <Compass className="w-4 h-4" /> {t("follow_trail")}
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>

    {/* Trail Follower Modal - outside Sheet for proper z-index */}
    {showTrailFollower && hasGpsTrack && (
      <TrailFollower
        trail={business}
        onClose={() => setShowTrailFollower(false)}
      />
    )}

    {/* Trail Follower Modal for Cammini - uses the start tappa with GPS track */}
    {showTrailFollower && !hasGpsTrack && hasCamminoTrack && (
      <TrailFollower
        trail={{
          ...camminoStart,
          name: business.cammino_name,
          description: `Percorso completo - ${camminoTappe.length} tappe`
        }}
        onClose={() => setShowTrailFollower(false)}
      />
    )}

    {/* Trail Follower Modal for Ciclo Turismo - uses the main route */}
    {showTrailFollower && business.category === "Ciclo Turismo" && !hasGpsTrack && hasCicloturismoTrack && (
      <TrailFollower
        trail={{
          ...cicloturismoMainRoute,
          name: cicloturismoMainRoute.name,
          description: cicloturismoMainRoute.description || "Ciclovia Parchi Calabria - 545 km"
        }}
        onClose={() => setShowTrailFollower(false)}
      />
    )}
    </>
  );
}
